import React, { useEffect, useState } from "react";
import { addLab, getAllLabs, updateLab, deleteLab } from "actions/action";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import ReactDOMServer from "react-dom/server";

export default function Labs() {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [labName, setLabName] = useState("");

  const fetchLabs = async () => {
    setLoading(true);
    try {
      const res = await getAllLabs();
      if (res?.err) throw new Error();
      const data = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.labs)
        ? res.labs
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];
      const normalized = data.map((lab) => ({
        id: lab.lab_id ?? lab.id,
        name: lab.lab_name ?? lab.name,
      }));
      setLabs(normalized);
      setError("");
    } catch (e) {
      setError("Failed to load labs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabs();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!labName.trim()) return;
    setCreating(true);
    try {
      const res = await addLab({ lab_name: labName.trim() });
      if (res?.err) throw new Error();
      setLabName("");
      await fetchLabs();
    } catch (e) {
      setError("Failed to create lab");
    } finally {
      setCreating(false);
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const startEdit = (lab) => {
    setEditingId(lab.id);
    setEditName(lab.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateLab(id, { lab_name: editName.trim() });
      cancelEdit();
      await fetchLabs();
    } catch {
      setError("Failed to update lab");
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete these records?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: ReactDOMServer.renderToString(
        <>
          Yes, delete it!
          <FontAwesomeIcon icon={faTrash} style={{ marginLeft: "10px" }} />
        </>
      ),
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteLab(id).then(() => {
          fetchLabs();
          Swal.fire({
            title: "Deleted!",
            text: "Lab record deleted successfully.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        }).catch(() => {
          setError("Failed to delete lab");
        });
      }
    });
  };

  return (
    <>
      <div className="relative">
        <div className="flex justify-between items-center px-4 mt-6">
          <p className="font-semibold text-lg text-white">Lab Details</p>
        </div>

        <div className="px-6 mt-4 mb-6">
          <form className="flex flex-row flex-wrap items-center" onSubmit={handleCreate}>
            <div className="relative flex w-full flex-wrap items-stretch gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  className="border-0 px-3 py-3 placeholder-blueGray-400 text-white relative bg-blueGray-700 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full"
                  placeholder="Enter lab name"
                  value={labName}
                  onChange={(e) => setLabName(e.target.value)}
                />
              </div>
              <Tooltip title="Create Lab" arrow>
                <button
                  type="submit"
                  disabled={creating}
                  className="bg-lightBlue-500 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150 disabled:opacity-60"
                >
                  Create Lab
                </button>
              </Tooltip>
            </div>
            {error && (
              <div className="text-red-500 text-sm mt-2">{error}</div>
            )}
          </form>
        </div>

        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-blueGray-700">
          <div className="block w-full overflow-x-auto">
            {loading ? (
              <div className="text-white p-4">Loading...</div>
            ) : labs.length === 0 ? (
              <div className="text-white p-4">No labs found.</div>
            ) : (
                <table className="items-center w-full bg-transparent border-collapse">
                  <thead>
                    <tr>
                      <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                        S.No.
                      </th>
                      <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                        Lab Name
                      </th>
                      <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {labs.map((lab, index) => (
                      <tr key={lab.id}>
                        <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                          {index + 1}
                        </td>
                        <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                          {editingId === lab.id ? (
                            <input
                              className="border px-2 py-1 rounded text-sm bg-blueGray-800 text-white"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                            />
                          ) : (
                            lab.name
                          )}
                        </td>
                        <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 text-center flex gap-4">
                          {editingId === lab.id ? (
                            <>
                              <button
                                onClick={() => saveEdit(lab.id)}
                                className="bg-emerald-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md mr-2"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="bg-blueGray-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Edit" arrow>
                                <motion.button
                                  whileHover={{ scale: 1.2 }}
                                  whileTap={{ scale: 0.9 }}
                                  type="button"
                                  onClick={() => startEdit(lab)}
                                  className="focus:outline-none"
                                  style={{ color: 'var(--accent-primary)' }}
                                >
                                  <FontAwesomeIcon
                                    icon={faPenToSquare}
                                    className="text-xl"
                                  />
                                </motion.button>
                              </Tooltip>
                              <Tooltip title="Delete" arrow>
                                <motion.button
                                  whileHover={{ scale: 1.2 }}
                                  whileTap={{ scale: 0.9 }}
                                  type="button"
                                  onClick={() => handleDelete(lab.id)}
                                  className="focus:outline-none mx-4"
                                  style={{ color: 'var(--danger)' }}
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="text-xl"
                                  />
                                </motion.button>
                              </Tooltip>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
    </>
  );
}
